if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isSubmitting || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // 法人プランのように最小数量がある商品で、手入力や空欄の値がそのまま送信されるのを防ぐ。
        this.enforceQuantityMinimum();

        this.handleErrorMessage();

        // 送信中の二重クリック防止は aria-disabled ではなくフラグで行う。
        // カートに追加するボタンに aria-disabled を付けると、Shopify の動的チェックアウト
        // ボタン（今すぐ購入）がその状態を追従して一緒に非活性になってしまうため。
        this.isSubmitting = true;
        this.submitButton.classList.add('loading');
        this.submitButton.setAttribute('aria-busy', 'true');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => this.capAddedQuantity(response))
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.isSubmitting = false;
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-busy');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // DCSオンライン＋（個人）のように1アカウントにつき1つの商品（data-max-quantity）は、
      // すでにカートにあるときにもう一度入れると同じ行にまとまって数量が2になる。追加の直後に上限へ戻す。
      // 戻せなかったときは通知（「購入手続きに進む」）を出さずにエラーを出し、カートで確かめてもらう
      // （カートを開くと cart.js がもう一度戻す）。
      capAddedQuantity(response) {
        const max = parseInt(this.dataset.maxQuantity);
        if (response.status || isNaN(max) || !(response.quantity > max)) return response;

        const body = { id: response.key, quantity: max };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body: JSON.stringify(body) })
          .then((changed) => changed.json())
          .then((cart) => {
            if (cart.status) throw new Error(cart.description || cart.message);
            return { ...response, quantity: max, sections: cart.sections || response.sections };
          })
          .catch((e) => {
            this.handleErrorMessage(window.cartStrings.error);
            throw e;
          });
      }

      // 数量入力は form 属性でこのフォームに紐づいているだけで DOM 上は外側にあるため、
      // form.elements 経由で取得して min を下回る値を補正する。
      enforceQuantityMinimum() {
        const quantityInput = this.form.elements.quantity;
        if (!quantityInput || typeof quantityInput.min === 'undefined') return;

        const min = parseInt(quantityInput.min);
        if (!min) return;

        const value = parseInt(quantityInput.value);
        if (isNaN(value) || value < min) {
          quantityInput.value = min;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
